import { useState, useCallback } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { debounce } from 'lodash'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'

import { useAlert } from 'components/Shared/Alert'
import filterGroupsAPI, {
  getCreateGroupPayload,
  getUpdateGroupPayload,
  ascGroupsParam,
} from 'api/ApiActions/Groups/FilterByGroup'
import { AdminUITheme } from 'types/AdminUiThemeMui'
import { fetchParticipantGroups } from 'redux/thunks/ParticipantGroups'
import { setActiveGroup } from 'redux/slices/ParticipantGroups'
import { useAppSelector, useAppDispatch } from 'redux/Hook'
import ParticipantInGroup from 'components/Participants/Group/ProjectsInGroup'
import { toggleHasFilterValueStatus } from 'redux/slices/FilterBuilder'

type ModalProps = {
  isOpen: boolean
  setModalVisibility: (isOpen: boolean) => void
}

const CreateModalDialog = ({ isOpen, setModalVisibility }: ModalProps) => {
  const { t } = useTranslation()
  const { showAlert } = useAlert()
  const theme: AdminUITheme = useTheme()
  const [isGroupNameInvalid, setgroupNameInvalidStatus] = useState(false)
  const [fieldMessage, setFieldMessage] = useState('')
  const dispatch = useAppDispatch()

  const segmentId = useAppSelector((state) => state.filterBuilder.segmentId)
  const { activeGroup } = useAppSelector((state) => state.participantGroups)
  const [groupName, setGroupName] = useState(activeGroup?.name || '')
  const [isValidationProcessing, setValidationProcessStatus] = useState(false)
  const [hasGroupNameChanged, setGroupNameChangedStatus] = useState(false)

  const handleCloseAndReset = () => {
    // Do not reset group name when there is an active group
    if (!activeGroup) {
      setGroupName('')
    }

    // Reset all the states
    setFieldMessage('')
    setModalVisibility(false)
    setgroupNameInvalidStatus(false)
    setValidationProcessStatus(false)
    setGroupNameChangedStatus(false)
    setGroupName(activeGroup?.name || '')
  }

  const handleClose = () => {
    setModalVisibility(false)
    setgroupNameInvalidStatus(false)
  }

  const handleError = () => {
    showAlert({ type: 'error', title: t('alert.error.400') })
  }

  const handleSuccess = (type = 'create') => {
    showAlert({
      type: 'success',
      title: t(`filters.participantGroup.${type}.successMessage`, {
        groupName,
      }),
      isHTML: true,
    })
  }

  const saveGroup = async () => {
    if (!groupName || fieldMessage) return

    // Remove trailing and leadnig space in group name
    const params = getCreateGroupPayload(groupName.trim(), segmentId)

    const result = await filterGroupsAPI.createGroup(params)
    const { code, status } = result

    if (status === 200) {
      handleSuccess()
      dispatch(fetchParticipantGroups(ascGroupsParam))

      // Disable group button
      dispatch(toggleHasFilterValueStatus(false))

      const { defaultSegment, name, id } = result.data.data

      dispatch(setActiveGroup({ defaultSegment, name, id }))
    } else if (code === 'ERR_BAD_REQUEST') {
      handleError()
    }

    setgroupNameInvalidStatus(false)
    handleClose()
  }

  const updateGroup = async () => {
    if (activeGroup) {
      const payload = getUpdateGroupPayload(segmentId)
      const response = await filterGroupsAPI.updateGroup({
        payload,
        segmentId: activeGroup.id,
      })

      if (response.status === 200) {
        // Disable group button
        dispatch(toggleHasFilterValueStatus(false))

        handleClose()
        handleSuccess('update')
      } else {
        handleError()
      }
    }
  }

  const handleSave = () => {
    if (isValidationProcessing || isGroupNameInvalid) return

    // Save as new group
    if (activeGroup && hasGroupNameChanged) {
      saveGroup()
      return
    }

    // Update the existing group
    if (activeGroup) {
      updateGroup()
      return
    }

    // Create new group
    saveGroup()
  }

  const validateGroupNameDebounce = useCallback(
    debounce((groupName) => validateGroupName(groupName), 300),
    [],
  )

  const validateGroupName = async (groupName: string) => {
    setValidationProcessStatus(true)

    // Only empty spaces
    if (groupName.replaceAll(' ', '') === '') {
      setgroupNameInvalidStatus(true)
      setFieldMessage('empty')
      setValidationProcessStatus(false)

      return
    }

    // 2. Empty field validation
    if (!groupName) {
      setgroupNameInvalidStatus(true)
      setFieldMessage('empty')
      setValidationProcessStatus(false)

      return
    }

    // 3. Invalid characters validation
    const invalidCharPattern = /[^a-zA-Z0-9 ]/g
    const hasInvalidChars = invalidCharPattern.test(groupName)

    if (hasInvalidChars) {
      setFieldMessage('invalidChars')
      setgroupNameInvalidStatus(true)
      setValidationProcessStatus(false)

      return
    }

    const response = await filterGroupsAPI.validateGroupName({
      name: groupName,
    })

    const isGroupNameValid = response.data === 'NOT_EXISTS' && groupName
    const isGroupNameTaken = response.data === 'ALREADY_EXISTS'

    if (isGroupNameValid) {
      setgroupNameInvalidStatus(false)
      setFieldMessage('')
    } else if (isGroupNameTaken) {
      setgroupNameInvalidStatus(true)
      setFieldMessage('groupNameTaken')
    }

    setValidationProcessStatus(false)
  }

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Restrict user to type when validation is processing
    if (isValidationProcessing) return
    let groupName = e.target.value

    // Allow only 1 space between words
    groupName = groupName.replace(/ +(?= )/g, '')
    setGroupName(groupName)
    setGroupNameChangedStatus(true)

    const removedLeadingTrailingSpace = groupName.trim()

    // 1. Unique group name validation
    validateGroupNameDebounce(removedLeadingTrailingSpace)
  }

  const getCharacterCountNode = () => {
    return (
      <Box
        component="span"
        sx={{ float: 'right' }}
      >{`${groupName.length}/98`}</Box>
    )
  }

  const getHelpText = () => {
    if (!fieldMessage && !isGroupNameInvalid) return getCharacterCountNode()

    switch (fieldMessage) {
      case 'groupNameTaken':
        return t('filters.participantGroup.create.groupNameTaken')
      case 'empty':
        return t('filters.participantGroup.create.groupHelperText')
      case 'invalidChars':
        return t('filters.participantGroup.create.invalidChars')

      default:
        return getCharacterCountNode()
    }
  }

  const ProjectsAccordion = () => {
    if (activeGroup && !hasGroupNameChanged) {
      return (
        <Box sx={{ mt: '1.5rem' }}>
          <ParticipantInGroup />
        </Box>
      )
    }

    return <></>
  }

  const SaveAsNewGroup = () => {
    if (hasGroupNameChanged && activeGroup) {
      return (
        <Box sx={{ mt: '1.5rem' }}>
          <Alert
            severity="info"
            sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
          >
            <Typography variant="body1">
              {t('filters.participantGroup.saveAsNew.alert')}
            </Typography>
          </Alert>
        </Box>
      )
    }

    return <></>
  }

  return (
    <Dialog open={isOpen} onClose={handleCloseAndReset}>
      <DialogContent
        sx={{
          ...theme.ParticipantGrid.createGroupDialogContent,
          height: '355.5px',
          overflowY: 'scroll',
        }}
      >
        <Stack spacing={3}>
          <Typography variant="h5" component="h5">
            {t('filters.participantGroup.create.saveAGroup')}
          </Typography>
          <Typography variant="body1" component="h5">
            {t('filters.participantGroup.create.saveGroupDescription')}
          </Typography>
          <TextField
            autoFocus
            error={isGroupNameInvalid}
            label={t('filters.participantGroup.create.groupName')}
            value={groupName}
            inputProps={{
              maxLength: 98,
            }}
            data-testid="filter-group-name-input"
            helperText={getHelpText()}
            onChange={handleChange}
            InputProps={{
              endAdornment: isValidationProcessing && (
                <CircularProgress size={24} />
              ),
            }}
            variant="standard"
          />
          <ProjectsAccordion />
          <SaveAsNewGroup />
        </Stack>
      </DialogContent>
      <DialogActions sx={theme.ParticipantGrid.createGroupDialogActions}>
        <Button
          sx={theme.ParticipantGrid.createGroupDialogCancelBtnMob}
          variant="outlined"
          onClick={handleCloseAndReset}
        >
          {t('common.cancel')}
        </Button>
        <Button
          sx={theme.ParticipantGrid.createGroupDialogCancelBtnDesk}
          variant="text"
          onClick={handleCloseAndReset}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          data-testid="filter-group-dialog-yes"
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateModalDialog
